/**
 * Input validation and sanitisation utilities for DrawLab.
 * Prevents injection, oversized payloads, and malformed data
 * reaching Firebase RTDB.
 */

const NICKNAME_MAX = 20;
const ROOM_NAME_MAX = 40;
const TEXT_CONTENT_MAX = 5000;
const STROKE_POINTS_MAX = 10000;
const GALLERY_MAX_SNAPSHOTS = 20;

// Game mode constants
const CHALLENGE_TIMER_OPTIONS = [60, 120, 180, 300]; // seconds
const RELAY_TIMER_OPTIONS = [10, 20, 30]; // seconds
const GAME_VOTE_MAX_SLIDER = 10;
const GAME_VOTE_MAX_STARS = 3;
const RELAY_REACTION_THROTTLE_MS = 2000;
const RELAY_MAX_REACTIONS = 50;

/**
 * Sanitise a nickname — alphanumeric, spaces, and hyphens only.
 * Returns the cleaned string, or empty string if nothing valid remains.
 */
export function sanitiseNickname(raw) {
  if (typeof raw !== 'string') return '';
  return raw
    .replace(/[^a-zA-Z0-9\s\-]/g, '')
    .trim()
    .slice(0, NICKNAME_MAX);
}

/**
 * Sanitise a room name — alphanumeric, spaces, hyphens, and basic punctuation.
 * Returns the cleaned string, or empty string if nothing valid remains.
 */
export function sanitiseRoomName(raw) {
  if (typeof raw !== 'string') return '';
  return raw
    .replace(/[^a-zA-Z0-9\s\-.,!?'()]/g, '')
    .trim()
    .slice(0, ROOM_NAME_MAX);
}

/**
 * Validate and cap the size of a stroke's points array.
 * Returns true if the stroke is within limits.
 * Mutates the array in place by truncating if oversized.
 */
export function validateStrokeSize(points) {
  if (!Array.isArray(points)) return true;
  if (points.length > STROKE_POINTS_MAX) {
    points.length = STROKE_POINTS_MAX;
  }
  return true;
}

/**
 * Validate text content length.
 * Returns the truncated string if it exceeds the maximum.
 */
export function validateTextContent(text) {
  if (typeof text !== 'string') return '';
  return text.slice(0, TEXT_CONTENT_MAX);
}

/**
 * Maximum number of gallery snapshots allowed per room.
 */
export {
  GALLERY_MAX_SNAPSHOTS,
  CHALLENGE_TIMER_OPTIONS,
  RELAY_TIMER_OPTIONS,
  GAME_VOTE_MAX_SLIDER,
  GAME_VOTE_MAX_STARS,
  RELAY_REACTION_THROTTLE_MS,
  RELAY_MAX_REACTIONS,
};
